import React from 'react';
import {
  PhoneIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { useInboundCallsPage } from './useInboundCallsPage';
import { TemperaturePill, DispositionBadge, GoalOutcomeIcon, FollowUpIndicator } from '../../components/intelligence';
import MultiSelectFilter from '../../components/filters/MultiSelectFilter';

const InboundCallsPage: React.FC = () => {
  const {
    calls,
    loading,
    error,
    phoneFilter,
    setPhoneFilter,
    tempFilter,
    setTempFilter,
    dispositionFilter,
    setDispositionFilter,
    statusFilter,
    setStatusFilter,
    goalFilter,
    setGoalFilter,
    temperatureOptions,
    dispositionOptions,
    statusOptions,
    goalOptions,
    currentPage,
    totalPages,
    formatPhoneNumber,
    formatDuration,
    openCallDetails,
    canViewDetails,
    handlePrevPage,
    handleNextPage,
    navigate,
  } = useInboundCallsPage();

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 sm:-right-64 -top-32 sm:-top-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"></div>
        <div className="absolute -left-32 sm:-left-64 top-1/2 -translate-y-1/2 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"></div>
      </div>
      <div className="relative mx-auto px-2 sm:px-3 lg:px-8 py-3 sm:py-4 lg:py-6">
        <div className="mb-3 sm:mb-4 lg:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg">
                <PhoneIcon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-white" />
              </div>
              <h1 className="text-base sm:text-lg lg:text-2xl xl:text-3xl font-bold text-slate-50">
                Inbound Calls
              </h1>
            </div>
            <button
              onClick={() => navigate('/inbound-configuration')}
              className="flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all touch-manipulation text-xs sm:text-sm lg:text-base"
            >
              <CogIcon className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Configuration</span>
              <span className="sm:hidden">Config</span>
            </button>
          </div>
          <p className="text-slate-300 text-xs sm:text-sm lg:text-base">
            Manage and review return calls from your leads
          </p>
        </div>

        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl p-2 sm:p-3 lg:p-4 rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50 mb-2 sm:mb-3 lg:mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">Phone Number</label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-3 w-3 sm:h-4 sm:w-4 absolute left-2 sm:left-3 top-2 sm:top-2.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by phone..."
                  value={phoneFilter}
                  onChange={(e) => setPhoneFilter(e.target.value)}
                  className="pl-7 sm:pl-9 w-full bg-slate-800/30 border border-slate-700/50 rounded-md px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
            <MultiSelectFilter
              label="Lead Temp"
              selectedValues={tempFilter}
              onChange={setTempFilter}
              options={temperatureOptions}
              allLabel="All temperatures"
            />
            <MultiSelectFilter
              label="Disposition"
              selectedValues={dispositionFilter}
              onChange={setDispositionFilter}
              options={dispositionOptions}
              allLabel="All dispositions"
            />
            <MultiSelectFilter
              label="Call Status"
              selectedValues={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              allLabel="All statuses"
            />
            <MultiSelectFilter
              label="Goal"
              selectedValues={goalFilter}
              onChange={setGoalFilter}
              options={goalOptions}
              allLabel="All goals"
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-400 rounded-md p-4 mb-6">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-200">Notice</h3>
                <p className="mt-1 text-sm text-red-100">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden">
          <div className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 border-b border-slate-700/30">
            <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-white">Recent Inbound Calls</h2>
          </div>

          {loading && calls.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto mb-4"></div>
              <p className="text-slate-300">Loading inbound calls...</p>
            </div>
          ) : calls.length === 0 ? (
            <div className="text-center py-8">
              <PhoneIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <h3 className="text-base font-medium text-white mb-2">No inbound calls found</h3>
              <p className="text-sm text-slate-300">
                {phoneFilter || tempFilter.length > 0 || dispositionFilter.length > 0 || statusFilter.length > 0 || goalFilter.length > 0
                  ? 'Try adjusting your filters to see more results.'
                  : 'Inbound calls will appear here when leads call you back.'}
              </p>
            </div>
          ) : (
            <div className="relative overflow-x-auto scrollbar-hide">
              {loading && calls.length > 0 && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 animate-pulse z-10"></div>
              )}
              <table className="w-full">
                <thead className="bg-slate-800/30">
                  <tr>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 text-left text-xs font-medium text-slate-400 uppercase">Lead</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Lead Temp</th>
                    <th className="hidden sm:table-cell px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Disposition</th>
                    <th className="hidden sm:table-cell px-2 sm:px-3 py-2 text-center text-xs font-medium text-slate-400 uppercase">Goal</th>
                    <th className="hidden md:table-cell px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Campaign</th>
                    <th className="hidden md:table-cell px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Date/Time</th>
                    <th className="hidden lg:table-cell px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/30">
                  {calls.map((call) => {
                    const navigable = canViewDetails(call);
                    return (
                      <tr
                        key={call.id}
                        className={`hover:bg-slate-800/30 transition-colors ${navigable ? 'cursor-pointer' : ''}`}
                        onClick={navigable ? () => openCallDetails(call) : undefined}
                      >
                        <td className="px-2 sm:px-3 lg:px-4 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm text-white font-medium truncate max-w-[140px]">
                              {call.caller_name || formatPhoneNumber(call.caller_phone)}
                            </span>
                            <FollowUpIndicator followUp={call.follow_up_action} />
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                            <span>{formatPhoneNumber(call.caller_phone)}</span>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2.5">
                          <TemperaturePill temperature={call.lead_temperature} />
                        </td>
                        <td className="hidden sm:table-cell px-2 sm:px-3 py-2.5">
                          <DispositionBadge disposition={call.disposition} />
                        </td>
                        <td className="hidden sm:table-cell px-2 sm:px-3 py-2.5 text-center">
                          <GoalOutcomeIcon outcome={call.goal_outcome} />
                        </td>
                        <td className="hidden md:table-cell px-2 sm:px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap truncate max-w-[160px]">
                          {call.matched_run_name || <span className="text-slate-500">—</span>}
                        </td>
                        <td className="hidden md:table-cell px-2 sm:px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                          {new Date(call.call_timestamp).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="hidden lg:table-cell px-2 sm:px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                          {formatDuration(call.call_duration)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 lg:py-4 pb-4 mb-1 border-t border-white/10">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 sm:gap-4">
                <div className="text-xs text-slate-300 text-center">
                  Page {currentPage + 1} of {totalPages}
                </div>
                <div className="flex justify-center space-x-1 sm:space-x-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 0}
                    className="px-2 sm:px-3 py-1 text-xs bg-gradient-to-r from-slate-800/30 to-slate-900/40 text-white border border-slate-700/50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:from-slate-700/40 hover:to-slate-800/50 hover:border-slate-600/50 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg touch-manipulation"
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages - 1}
                    className="px-2 sm:px-3 py-1 text-xs bg-gradient-to-r from-slate-800/30 to-slate-900/40 text-white border border-slate-700/50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:from-slate-700/40 hover:to-slate-800/50 hover:border-slate-600/50 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg touch-manipulation"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default InboundCallsPage;
